// Doable Constants

import { Clock, Activity, Brain, Users, Menu, Folder, FolderKanban, Tag, ListChecks } from 'lucide-react';

// API Configuration
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';

export const ABLE_COLORS = {
  // Primary palette
  primary: '#3D9A8B',      // softened teal - main actions/headers
  secondary: '#5BA88F',    // warm teal - hover states/secondary actions
  accent: '#F59E0B',       // amber - warm energy pop for highlights
  warm: '#F97316',         // coral-orange - encouraging elements

  // Backgrounds (warm, not clinical)
  bg: '#FAF7F2',           // warm cream - main background
  bgSubtle: '#F8F4ED',     // warm beige - section backgrounds
  neutral: '#FFFFFF',      // pure white - card backgrounds

  // Text hierarchy
  textPrimary: '#1F2937',  // dark gray - headings, important text
  textSecondary: '#4B5563', // medium gray - body text
  textMuted: '#9CA3AF',    // light gray - captions, hints

  // Borders and shadows
  border: '#E8E4DD',       // warm beige border (standard)
  borderLight: '#F0EBE4',  // very light warm border for cards
  borderMedium: '#D1D5DB', // medium gray border (for inputs/modals)
  cardShadow: '0 2px 8px rgba(0,0,0,0.04)',  // soft shadow
  cardShadowHover: '0 4px 12px rgba(0,0,0,0.08)',  // hover shadow
  cardShadowElevated: '0 8px 24px rgba(13, 148, 136, 0.12), 0 4px 8px rgba(0, 0, 0, 0.04)', // expanded/active state

  // Interactive feedback backgrounds
  hoverBg: '#F9FAFB',      // subtle hover background
  errorBg: '#FEE2E2',      // light red for error states
  successBg: '#D1FAE5',    // light green for success states

  // Spacing and radii (as CSS values)
  // 3-tier system: small (chips/pills), medium (buttons/inputs), large (cards)
  radiusSmall: '8px',      // chips, pills, small tags
  radiusMedium: '12px',    // buttons, inputs, dropdowns
  radiusLarge: '16px',     // cards, containers
  // Legacy aliases (for backwards compatibility)
  cardRadius: '16px',
  cardRadiusLarge: '16px',
  buttonRadius: '12px',
  chipRadius: '8px',       // updated to match radiusSmall

  // Energy context colors (TASK REQUIREMENTS - what the task needs)
  // Metaphor: Elements of nature - distinct from state colors
  energy: {
    timeOfDay: '#F59E0B',   // amber/sun - time-based tasks
    physical: '#059669',    // emerald - earth/body energy required
    mental: '#F97316',      // coral/fire - brain energy required
    social: '#8B5CF6'       // violet - connection energy required
  },

  // Priority colors (MOMENTUM/FLOW - not urgency/pressure)
  // Rich, warm saturation matching the mood palette
  priority: {
    now: '#3D9A8B',         // teal - "I'm doing it!" (brand color)
    next: '#D99A3A',        // rich amber - warm anticipation
    later: '#7A8690',       // warm slate - calm, no pressure
    waiting: '#A8A29E',     // warm stone - paused, patient
    someday: '#9988B0',     // muted violet - dreams/aspirations
    scheduled: '#5A8EB8'    // muted blue - trusted to calendar
  },

  // Semantic colors (system feedback)
  // Muted but still distinct for visibility
  semantic: {
    success: '#4FA06E',     // rich sage - completed, positive
    warning: '#D97706',     // warm amber - attention needed
    error: '#C9555A',       // muted red - problems (still stands out)
    info: '#5A8EB8',        // muted blue - informational
    blocked: '#92400E',     // brown - blocked state text
    blockedBg: '#FEF3C7'    // warm yellow - blocked state background
  },

  // State check-in colors (USER FEELINGS - vibrant on white)
  // Boosted saturation for visibility on neutral backgrounds
  state: {
    creative: '#D97035',    // vibrant orange - playful flow
    mental: '#7B68B8',      // vibrant violet - focused thinking
    emotional: '#D65A6A',   // vibrant rose - warm feelings
    social: '#2E8B9A',      // vibrant teal - connection
    physical: '#3D9A6A',    // vibrant green - body awareness
    rest: '#7A6B5A'         // warm brown - peaceful recovery
  },

  // Feeling scale (1-5) - matches mood palette
  // Boosted for visibility on white backgrounds
  feeling: {
    1: '#A07A8D',           // deeper mauve - safe to rest
    2: '#B8624A',           // deeper terracotta - grounded
    3: '#9A8B70',           // deeper oat - stable
    4: '#6A9460',           // deeper sage - growing
    5: '#5A90A0'            // deeper sky - clear
  },

  // Location colors (PHYSICAL SPACES - visible on white)
  // Boosted for visibility while staying grounded
  location: {
    couch: '#B86E1A',       // warm brown - cozy comfort
    desk: '#5A6570',        // slate - focused calm
    moving: '#2D8A6E',      // teal - active vitality
    other: '#6B6560'        // stone - flexible
  },

  // Mood level colors (1-5 icon scale)
  // Boosted for visibility on white backgrounds
  mood: {
    1: '#A07A8D',           // deeper mauve - safe to rest
    2: '#B8624A',           // deeper terracotta - grounded
    3: '#9A8B70',           // deeper oat - stable
    4: '#6A9460',           // deeper sage - growing
    5: '#5A90A0'            // deeper sky - clear
  },

  // Energy level colors (Low/Med/High)
  // Boosted for visibility on white backgrounds
  energyLevel: {
    low: '#A07A8D',         // deeper mauve - safe to be low
    med: '#9A8B70',         // deeper oat - stable middle
    high: '#5A90A0'         // deeper sky - clear/spacious
  }
};

export const ACTIONS = ['Unprioritized', 'Now', 'Next', 'Later', 'Waiting', 'Someday', 'Scheduled'];

export const CONTEXT_OPTIONS = {
  priority: ['Unprioritized', 'Now', 'Next', 'Later', 'Waiting', 'Someday', 'Scheduled'],
  area: ['Home', 'Work', 'Games', 'Apps', 'Family', 'Finance', 'Health', 'Social'],
  timeOfDay: ['Morning', 'Midday', 'Afternoon', 'Evening', 'Night', 'Anytime'],
  physical: ['Couch Energy', 'Standing', 'Desk Energy', 'Movement'],
  mental: ['Creative Flow', 'Admin Brain', 'Problem-Solving', 'Autopilot'],
  social: ['Solo', 'Parallel Play', 'Social Energy']
};

export const STATUS_OPTIONS = ['all', 'active', 'done', 'unblocked', 'blocked'];

export const FILTER_CONFIG = [
  { key: 'priority', icon: Menu, label: 'Priority' },
  { key: 'area', icon: Folder, label: 'Area' },
  { key: 'project', icon: FolderKanban, label: 'Project' },
  { key: 'release', icon: Tag, label: 'Release' },
  { key: 'workflow', icon: ListChecks, label: 'Workflow' },
  { key: 'timeOfDay', icon: Clock, label: 'Time of Day' },
  { key: 'physical', icon: Activity, label: 'Physical' },
  { key: 'mental', icon: Brain, label: 'Mental' },
  { key: 'social', icon: Users, label: 'Social' }
];

// App version - update this when releasing new versions
export const APP_VERSION = '1.1.0';

export const ENCOURAGING_MESSAGES = [
  "You're exactly where you need to be 🦋",
  "One thing at a time. That's enough. ✨",
  "Progress, not perfection. 🌊",
  "Your pace is perfect. 💙",
  "Every small step counts. 🌟",
  "You're doing better than you think. 🌸"
];
